import type { AiChatMessage } from '@/api/miniapp/types';

/**
 * Локальные вложения переписки: записи голоса и отправленные файлы.
 *
 * Сервер их в историю не кладёт, поэтому лента собирается из двух слоёв:
 * истории с сервера и вложений поверх неё. Место вложения считается только от
 * истории — если считать от готовой ленты, номера сдвигаются от собственных
 * добавлений, и при каждой отправке все прошлые записи собираются внизу.
 */

/**
 * Отправленное вложение: запись голоса или файл.
 *
 * Сервер отдаёт расшифровку голоса и ответ агента на файл, но самих
 * отправленных материалов в истории нет. Без локальной отметки отправка
 * выглядела бы как бездействие: сообщение пользователя пустое, а файл нигде не
 * виден. `userIndex` — порядковый номер среди своих сообщений: по нему
 * вложение прикрепляется к той реплике истории, из которой оно получилось.
 */
export interface LocalUpload {
  kind: 'voice' | 'file';
  /** Адрес записи для проигрывателя; у файла его нет. */
  url?: string;
  durationMs?: number;
  filename?: string;
  /** Номер среди своих реплик истории — по нему вложение к ней прикрепляется. */
  userIndex: number;
  /** Место в истории на момент отправки — туда вложение и встаёт. */
  position: number;
  /**
   * Сервер ещё обрабатывает вложение.
   *
   * Отдельный признак, а не «ещё не показано в истории»: расшифрованная запись
   * может так и не появиться репликой, и тогда она считалась бы
   * обрабатываемой вечно.
   */
  sending: boolean;
}

/**
 * Совмещение истории с локальными вложениями.
 *
 * Вложение, для которого сервер уже показал свою реплику, прикрепляется к ней.
 * Остальные встают туда, где были отправлены: если складывать их в конец, то
 * ответ ассистента оказывался бы выше записи, на которую отвечает, а при
 * каждой новой отправке вся прошлая переписка голосом собиралась бы внизу.
 *
 * Считать положение можно только от истории без локальных вложений — иначе
 * номера сдвигались бы от собственных же добавлений.
 */
export function applyUploads(history: AiChatMessage[], uploads: LocalUpload[]): AiChatMessage[] {
  if (uploads.length === 0) return history;

  const unclaimed = [...uploads];
  let userIndex = 0;

  const merged = history.map((message) => {
    if (message.role !== 'user') return message;
    const index = unclaimed.findIndex((upload) => upload.userIndex === userIndex);
    userIndex += 1;
    if (index === -1) return message;
    const [upload] = unclaimed.splice(index, 1);
    return { ...message, ...decorate(upload) };
  });

  // Сервер такие вложения в историю не кладёт: держим их на своих местах
  const result = [...merged];
  for (const [offset, upload] of unclaimed.entries()) {
    result.splice(Math.min(upload.position + offset, result.length), 0, bubbleOf(upload));
  }
  return result;
}

/**
 * Пузырь вложения, которого ещё нет в истории.
 *
 * Запись без расшифровки помечена обрабатываемой, даже когда загрузка уже
 * прошла: сервер отвечает раньше, чем кладёт расшифровку в историю, и в этом
 * промежутке запись выглядела законченной, но пустой — человек принимал
 * чужой ответ ниже за ответ на неё.
 */
function bubbleOf(upload: LocalUpload): AiChatMessage {
  return {
    role: 'user',
    content: '',
    pending: upload.kind === 'voice' ? true : upload.sending,
    ...decorate(upload),
  };
}

/** Куда встанет следующее вложение и к какой реплике оно относится. */
export function placeOf(history: AiChatMessage[]): { userIndex: number; position: number } {
  return {
    userIndex: history.filter((message) => message.role === 'user').length,
    position: history.length,
  };
}

function decorate(upload: LocalUpload): Partial<AiChatMessage> {
  return upload.kind === 'voice'
    ? { audioUrl: upload.url, voiceMs: upload.durationMs }
    : { sentFile: upload.filename };
}

/**
 * Освобождение памяти под записи.
 *
 * Адреса blob живут до явного отзыва: без этого каждое переоткрытие диалога
 * оставляло бы записи в памяти вкладки.
 */
export function releaseUploads(uploads: LocalUpload[]): void {
  for (const upload of uploads) if (upload.url) URL.revokeObjectURL(upload.url);
}

/** Отметить вложение обработанным: сервер его принял. */
export function markSent(uploads: LocalUpload[], target: LocalUpload): LocalUpload[] {
  return uploads.map((upload) => (upload === target ? { ...upload, sending: false } : upload));
}
